import styled from '@emotion/styled';
import moment from 'moment';
import {
  TitreBidAskType,
  TitreEntity,
} from '../../../../entities/titre.entity';
import { PRIMARY } from '../../../../shared/colors';
import { formatNum } from '../../../../shared/constants';

const BID_COLOR = '#008f4f';
const ASK_COLOR = '#d92d20';

const Container = styled.div`
  height: 100%;

  > .top {
    background:
      linear-gradient(135deg, rgba(255, 79, 31, 0.16), transparent 42%),
      ${PRIMARY};
    padding: 8px 12px;
    font-family: Poppins-B;
    font-size: 15px;
    text-transform: uppercase;
    color: var(--mt-warning);
  }

  > .content {
    height: calc(100% - 38px);
    box-sizing: border-box;
    padding: 10px 12px 12px;
    font-family: Poppins-R;
    overflow-x: auto;
    overflow-y: auto;

    .instrument-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      padding: 9px 10px;
      margin-bottom: 8px;
      border: 1px solid var(--mt-border);
      border-radius: 8px;
      background:
        linear-gradient(135deg, rgba(255, 176, 0, 0.08), transparent 48%),
        var(--mt-surface);
    }

    .instrument-code {
      font-family: Poppins-B;
      font-size: 14px;
      color: var(--mt-text);
      line-height: 1.25;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .instrument-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 5px;
      margin-top: 0;
      justify-content: flex-end;
    }

    .instrument-meta span {
      padding: 2px 6px;
      border: 1px solid var(--mt-border);
      border-radius: 999px;
      background-color: var(--mt-surface-strong);
      color: var(--mt-muted);
      font-size: 9px;
      text-transform: uppercase;
    }

    .quote-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 6px;
      margin-bottom: 8px;
    }

    .quote-card {
      padding: 8px 9px;
      border: 1px solid var(--mt-border);
      border-radius: 8px;
      background-color: var(--mt-card);
    }

    .quote-card span,
    .detail-item span {
      display: block;
      color: var(--mt-muted);
      font-size: 9px;
      letter-spacing: 0;
      text-transform: uppercase;
    }

    .quote-card strong {
      display: block;
      margin-top: 2px;
      font-family: Poppins-B;
      font-size: 14px;
      line-height: 1.1;
    }

    .quote-card.bid strong {
      color: ${BID_COLOR};
    }

    .quote-card.ask strong {
      color: ${ASK_COLOR};
    }

    .section-label {
      margin-bottom: 6px;
      color: var(--mt-warning);
      font-family: Poppins-B;
      font-size: 11px;
      text-transform: uppercase;
    }

    .detail-grid {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      overflow: hidden;
      border: 1px solid var(--mt-border);
      border-radius: 8px;
      background-color: var(--mt-card);
    }

    .detail-item {
      min-width: 0;
      padding: 8px 9px;
      border-right: 1px solid var(--mt-border);
      border-bottom: 1px solid var(--mt-border);
    }

    .detail-item:nth-of-type(4n) {
      border-right: 0;
    }

    .detail-item:nth-last-of-type(-n + 4) {
      border-bottom: 0;
    }

    .detail-item strong {
      display: block;
      margin-top: 2px;
      color: var(--mt-text);
      font-family: Poppins-B;
      font-size: 11px;
      line-height: 1.2;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &::-webkit-scrollbar {
      width: 0.5vw;
    }

    &::-webkit-scrollbar-thumb {
      background-image: linear-gradient(to bottom, #eceff1, #eceff1);
      border-radius: 10px;
    }
  }

  @media (max-width: 760px) {
    > .content {
      max-height: none;
    }

    > .content .instrument-head {
      align-items: flex-start;
      flex-direction: column;
    }

    > .content .instrument-meta {
      justify-content: flex-start;
    }

    > .content .detail-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    > .content .detail-item:nth-of-type(4n) {
      border-right: 1px solid var(--mt-border);
    }

    > .content .detail-item:nth-of-type(2n) {
      border-right: 0;
    }
  }
`;

export const InstrumentDetail = ({
  selectedTitre,
  titreBidAsk,
}: {
  selectedTitre?: TitreEntity;
  titreBidAsk: TitreBidAskType;
}) => {
  const formatDate = (value?: string) =>
    value ? moment(value).format('DD/MM/YYYY') : '--';

  const detailRows = [
    { label: 'Pays émetteur', value: selectedTitre?.pays || '--' },
    { label: 'Instrument', value: selectedTitre?.instrument || '--' },
    { label: "Date d'émission", value: formatDate(selectedTitre?.dateEmission) },
    { label: 'Date maturité', value: formatDate(selectedTitre?.dateMaturite) },
    { label: 'Durée', value: selectedTitre?.dureeMaturite || '--' },
    { label: 'Fréquence', value: selectedTitre?.frequencePaiement || '--' },
    {
      label: 'Valeur nominale',
      value: selectedTitre?.valeurNominale
        ? `${formatNum(selectedTitre.valeurNominale)} XAF`
        : '--',
    },
    { label: 'Coupon', value: `${selectedTitre?.tauxMarginal || '--'} %` },
  ];

  return (
    <Container>
      <div className='top'>Détails instrument</div>

      <div className='content'>
        <div className='instrument-head'>
          <div className='instrument-code'>{selectedTitre?.code || '--'}</div>
          <div className='instrument-meta'>
            <span>{selectedTitre?.pays || 'Pays --'}</span>
            <span>{selectedTitre?.instrument || 'Instrument --'}</span>
          </div>
        </div>

        <div className='quote-grid'>
          <div className='quote-card bid'>
            <span>Bid</span>
            <strong>{formatNum(titreBidAsk.bid)} XAF</strong>
          </div>
          <div className='quote-card ask'>
            <span>Ask</span>
            <strong>{formatNum(titreBidAsk.ask)} XAF</strong>
          </div>
        </div>

        <div className='section-label'>Caractéristiques</div>
        <div className='detail-grid'>
          {detailRows.map((row) => (
            <div className='detail-item' key={row.label}>
              <span>{row.label}</span>
              <strong>{row.value}</strong>
            </div>
          ))}
        </div>
      </div>
    </Container>
  );
};
